import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import type { Case } from '../domain/initial-cases';
import { CASE_STAGES, SIDE_STATE_LABELS, caseProgress, handoffLine, stageLabel } from '../domain/case-progress';

// #766 — THE CASE PROGRESS TRACKER: a stage banner, the stepper, and the open
// items of the current stage, pinned in the case header above the tab row so it
// is on every tab in every context (OA, VA and all tabs).
//
// Everything it says comes from `caseProgress` (domain/case-progress.ts), whose
// open items are the gates' own predicates. An item with a tab is a button that
// opens that tab, because that is where it is resolved; the sync worker's steps
// have no tab and render as text.
//
// The handoff line is the record of who sent the case to vetting and when —
// stamped by the API from the sender's sign-in (A030), not by this client.
export function CaseProgressTracker({ c, onOpenTab }: { c: Case; onOpenTab: (tab: string) => void }) {
  const p = caseProgress(c);
  const current = CASE_STAGES.findIndex(s => s.key === p.stage);
  const handoff = handoffLine(c, fmtDateTime);

  return (
    <section aria-label="Case progress" data-testid="case-progress" style={{ ...styles.card, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span
          data-testid="stage-banner"
          style={{ ...styles.badge, background: p.ended ? C.neutralBg : C.goldBg, color: p.ended ? C.neutral : C.goldText, fontSize: 12 }}
        >{p.banner}</span>
        {p.sideState && (
          <span data-testid="side-state" style={{ ...styles.badge, background: C.warnBg, color: C.warn }}>
            ↳ Detour: {SIDE_STATE_LABELS[p.sideState]}
          </span>
        )}
        {handoff && <span data-testid="handoff-line" style={{ fontSize: 12, color: C.textMuted }}>{handoff}</span>}
      </div>

      <ol aria-label="Stages" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, listStyle: 'none', margin: 0, padding: 0 }}>
        {CASE_STAGES.map((s, i) => {
          const isCurrent = i === current;
          const complete = i < current || (isCurrent && p.done);
          return (
            <li
              key={s.key}
              data-testid={`stage-${s.key}`}
              data-state={complete ? 'done' : isCurrent ? 'current' : 'pending'}
              aria-current={isCurrent ? 'step' : undefined}
              title={s.detail}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 16, fontSize: 12,
                border: `2px solid ${isCurrent ? C.gold : C.border}`,
                background: isCurrent ? C.goldBg : 'transparent',
                color: complete ? C.success : isCurrent ? C.goldText : C.textDim,
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              <span aria-hidden="true">{complete ? '✓' : i + 1}</span>
              {s.label}
            </li>
          );
        })}
      </ol>

      {p.openItems.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Open at this stage</div>
          <ul data-testid="open-items" style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
            {p.openItems.map(item => (
              <li key={item.key} data-testid={`open-item-${item.key}`}>
                {item.tab ? (
                  <button
                    type="button"
                    style={{ ...styles.btnUnstyled, color: C.goldText, textDecoration: 'underline' }}
                    onClick={() => onOpenTab(item.tab!)}
                  >{item.label}</button>
                ) : (
                  <span style={{ color: C.textMuted }}>{item.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// #766 — the queues' "Stage / next step" cell (Cases and Onboarding): the same
// derivation as the tracker, cut down to the stage, a detour marker and the
// first open item.
export function CaseStageCell({ c }: { c: Case }) {
  const p = caseProgress(c);
  return (
    <span style={{ fontSize: 12 }} data-testid="stage-cell">
      {stageLabel(p.stage)}
      {p.sideState && <span style={{ color: C.warn }}> ({SIDE_STATE_LABELS[p.sideState]})</span>}
      {p.openItems[0] && <span style={{ color: C.textMuted }}> · {p.openItems[0].label}</span>}
    </span>
  );
}
